/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { apiPostAuth } from '../../amplify/restClient';
import { getAuthHeaders } from '../../amplify/authUtilities';
import { allTransactionsProjectView } from './globalSelectors/overarching';
import { globalBaselineView } from './globalSelectors';
import { buildScopesFromTransactions, normalizeDates, normalizeScopes } from './sharedThunks/quoteScopes';
import getQuotes, { getIsolatedQuotes } from './sharedThunks/getQuotes';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const NAMED_DATE_KEYS = new Set(['current', 'baseline', 'referenceBaseline']);

function toNumericDate(date) {
  if (typeof date === 'number' && Number.isFinite(date)) return date;
  if (typeof date === 'string' && /^\d+$/.test(date)) return Number(date);
  return null;
}

function isNamedDate(date) {
  return NAMED_DATE_KEYS.has(String(date));
}

function getNamedRequiredDates(state) {
  const { baseline, referenceBaseline, current } = globalBaselineView(state);
  return normalizeDates([baseline, referenceBaseline, current]);
}

// Normalize global quote rows into canonical map shape: global[assetId][date] = row.
function normalizeGlobalRows(rows = []) {
  return (rows || []).reduce((acc, quote) => {
    if (!quote?.assetId) return acc;
    if (isNamedDate(quote.date)) return acc;
    const numericDate = toNumericDate(quote.date);
    if (numericDate === null) return acc;

    if (!acc[quote.assetId]) acc[quote.assetId] = {};
    acc[quote.assetId][numericDate] = { ...quote, date: numericDate };
    return acc;
  }, {});
}

// Normalize isolated quote rows into canonical map shape: isolated[projectId][assetId][date] = row.
function normalizeIsolatedRows(rows = []) {
  return (rows || []).reduce((acc, quote) => {
    if (!quote?.projectId || !quote?.assetId) return acc;
    if (isNamedDate(quote.date)) return acc;
    const numericDate = toNumericDate(quote.date);
    if (numericDate === null) return acc;

    if (!acc[quote.projectId]) acc[quote.projectId] = {};
    if (!acc[quote.projectId][quote.assetId]) acc[quote.projectId][quote.assetId] = {};
    acc[quote.projectId][quote.assetId][numericDate] = { ...quote, date: numericDate };
    return acc;
  }, {});
}

function flattenGlobalMap(globalMap = {}) {
  return Object.entries(globalMap).flatMap(([assetId, dates]) => Object.entries(dates || {}).map(([date, quote]) => ({ ...quote, assetId, date: toNumericDate(date) || quote.date })));
}

function flattenIsolatedMap(isolatedMap = {}) {
  return Object.entries(isolatedMap).flatMap(([projectId, assets]) => Object.entries(assets || {}).flatMap(([assetId, dates]) => Object.entries(dates || {}).map(([date, quote]) => ({
    ...quote,
    projectId,
    assetId,
    date: toNumericDate(date) || quote.date,
  }))));
}

function normalizeGlobalPartition(partition) {
  if (Array.isArray(partition)) return normalizeGlobalRows(partition);
  if (partition && typeof partition === 'object') return normalizeGlobalRows(flattenGlobalMap(partition));
  return {};
}

function normalizeIsolatedPartition(partition) {
  if (Array.isArray(partition)) return normalizeIsolatedRows(partition);
  if (partition && typeof partition === 'object') return normalizeIsolatedRows(flattenIsolatedMap(partition));
  return {};
}

function mergeGlobalRows(globalState, rows = []) {
  rows.forEach((quote) => {
    const numericDate = toNumericDate(quote?.date);
    if (!quote?.assetId || numericDate === null || isNamedDate(quote.date)) return;
    if (!globalState[quote.assetId]) globalState[quote.assetId] = {};
    globalState[quote.assetId][numericDate] = { ...quote, date: numericDate };
  });
}

function replaceGlobalPartitions(globalState, requestedAssets = []) {
  const requestedAssetIds = [...new Set((requestedAssets || []).map((asset) => asset?.assetId).filter(Boolean))];
  requestedAssetIds.forEach((assetId) => {
    delete globalState[assetId];
  });
}

function mergeIsolatedRows(isolatedState, rows = []) {
  rows.forEach((quote) => {
    const numericDate = toNumericDate(quote?.date);
    if (!quote?.projectId || !quote?.assetId || numericDate === null || isNamedDate(quote.date)) return;
    if (!isolatedState[quote.projectId]) isolatedState[quote.projectId] = {};
    if (!isolatedState[quote.projectId][quote.assetId]) isolatedState[quote.projectId][quote.assetId] = {};
    isolatedState[quote.projectId][quote.assetId][numericDate] = { ...quote, date: numericDate };
  });
}

function deleteGlobalQuote(globalState, assetId, date) {
  if (!globalState?.[assetId]) return;
  delete globalState[assetId][date];
  if (Object.keys(globalState[assetId]).length === 0) delete globalState[assetId];
}

function deleteIsolatedQuote(isolatedState, projectId, assetId, date) {
  if (!isolatedState?.[projectId]?.[assetId]) return;
  delete isolatedState[projectId][assetId][date];
  if (Object.keys(isolatedState[projectId][assetId]).length === 0) delete isolatedState[projectId][assetId];
  if (Object.keys(isolatedState[projectId]).length === 0) delete isolatedState[projectId];
}

const initialState = {
  global: {},
  isolated: {},
  // Keep basic sync metadata in slice for future scoped refresh bookkeeping.
  meta: {
    schemaVersion: 2,
    lastGlobalSyncAt: null,
    lastIsolatedSyncAt: null,
    lastScopes: {},
  },
};

export async function prepCallToApi(body) {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const myInit = {
    headers,
  };
  if (body !== undefined) {
    myInit.body = body;
  }
  return myInit;
}

export const putQuotes = createAsyncThunk('quotes/putQuotes', async ({ body, category, accountId }, { dispatch, getState }) => {
  const payload = await prepCallToApi(body);
  const response = await apiPostAuth('myAPI', 'quotes/put', payload);
  const state = getState();
  const requiredDates = getNamedRequiredDates(state);
  const getQuotesScope = buildScopesFromTransactions({
    transactions: (state.data?.[category]?.transactions || []).filter((t) => t.accountId === accountId),
    requiredDates,
  });
  await dispatch(getQuotes({ scopes: getQuotesScope })); // wait for getQuotes to finish
  return response.quotes;
});

// expects:
// - arrayOfQuotes, each of which includes importFlag;
// - accountId (optional, to limit the assetIds to those present in a specific account, like we can during updates from Grid)
export const putOrDeleteQuotes = createAsyncThunk('quotes/putOrDeleteQuotes', async ({ arrayOfQuotes, accountId }, { dispatch, getState }) => {
  const allDistinctIncomingDates = [...new Set(arrayOfQuotes.map((q) => q.date))];
  const allDistinctIncomingAssetIds = [...new Set(arrayOfQuotes.map((q) => q.assetId))];
  const earliestQuoteDate = Math.min(...arrayOfQuotes.map((q) => q.date)); // Math.min requires a comma-separated list of arguments
  const putItems = arrayOfQuotes.filter((q) => q.importFlag !== 'delete');
  const payloadPut = await prepCallToApi(putItems);

  const deleteItems = arrayOfQuotes.filter((q) => q.importFlag === 'delete');
  const payloadDelete = await prepCallToApi(deleteItems);

  const promises = [];

  if (putItems.length > 0) promises.push(apiPostAuth('myAPI', 'quotes/put', payloadPut));
  if (deleteItems.length > 0) promises.push(apiPostAuth('myAPI', 'quotes/delete', payloadDelete));

  await Promise.all(promises);

  const state = getState();

  // we have changed some quotes above, so we need to run getQuotes to refresh them in state
  // select all assetIds in the transactions, and if we are only doing that for an account, filter by that account
  // in addition, only get dates which happen after the earliest quote date (so that we don't have to refresh the entire past where nothing has changed)
  // we try to find out the transactions so that we know which dates and assetIds / providerAssetIds / currencies to get quotes for
  const transactionsInScope = allTransactionsProjectView(state).filter((t) => (accountId ? t.accountId === accountId : true) && t.date >= earliestQuoteDate && t.category !== 'deposits');

  // FIXME this doesn't seem to be the most efficient way of doing this
  // there are cases when there are no transactions after the earliestQuoteDate (e.g. when adding realEstate valuation)
  // to handle this, fetch all assetId-date pairs for the assetIds for which we are updating the quotes
  if (accountId && transactionsInScope.length === 0) {
    const accountTransactions = allTransactionsProjectView(state).filter((t) => allDistinctIncomingAssetIds.includes(t.assetId));
    if (accountTransactions.length > 0) {
      transactionsInScope.push(...accountTransactions);
    }
  }
  const requiredDates = normalizeDates(getNamedRequiredDates(state).concat(allDistinctIncomingDates));
  const transactionScopes = buildScopesFromTransactions({
    transactions: transactionsInScope,
    requiredDates,
  });

  // remove deleted items from store immediately (async refresh below will repopulate)
  if (deleteItems.length > 0) dispatch({ type: 'quotes/removeQuotes', payload: deleteItems });

  // Handle manual/non-transaction assets by adding explicit fallback scopes for incoming assetIds.
  const existingAssetIds = new Set(transactionScopes.map((scope) => scope?.asset?.assetId).filter(Boolean));
  const fallbackFromDate = requiredDates.length > 0 ? Math.min(...requiredDates) : earliestQuoteDate;
  const fallbackToDate = requiredDates.length > 0 ? Math.max(...requiredDates) : earliestQuoteDate;
  const fallbackScopes = allDistinctIncomingAssetIds
    .filter((assetId) => !!assetId && !existingAssetIds.has(assetId))
    .map((assetId) => {
      const incomingQuote = arrayOfQuotes.find((quote) => quote.assetId === assetId);
      return {
        asset: {
          assetId,
          providerAssetId: null,
          ...(incomingQuote?.currency ? { currency: incomingQuote.currency } : {}),
        },
        fromDate: fallbackFromDate,
        toDate: fallbackToDate,
        requiredDates: requiredDates.length > 0 ? requiredDates : [fallbackToDate],
        includeManualQuotes: true,
      };
    });

  const scopes = normalizeScopes(transactionScopes.concat(fallbackScopes));
  await dispatch(getQuotes({ scopes }));

  return arrayOfQuotes;
});

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    removeQuotes(state, action) {
      const toDelete = action.payload || [];
      if (!toDelete.length) return;
      // Remove by nested path; if projectId is omitted keep legacy behavior and remove matching global + isolated entries.
      toDelete.forEach((quote) => {
        const numericDate = toNumericDate(quote?.date);
        if (!quote?.assetId || numericDate === null) return;

        if (quote.projectId) {
          deleteIsolatedQuote(state.isolated, quote.projectId, quote.assetId, numericDate);
          return;
        }

        deleteGlobalQuote(state.global, quote.assetId, numericDate);
        Object.keys(state.isolated || {}).forEach((projectId) => {
          deleteIsolatedQuote(state.isolated, projectId, quote.assetId, numericDate);
        });
      });
    },
    hydrate(state, action) {
      const incoming = action.payload || {};
      // Hydration migration: accept legacy arrays or nested maps and normalize to canonical nested state.
      return {
        ...state,
        ...incoming,
        global: normalizeGlobalPartition(incoming.global ?? state.global),
        isolated: normalizeIsolatedPartition(incoming.isolated ?? state.isolated),
        meta: {
          ...state.meta,
          ...(incoming.meta || {}),
          schemaVersion: 2,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuotes.fulfilled, function getQuotesFulfilledCallback(state, action) {
        if (!action.payload || action.payload?.status === 'error') return;

        const rows = Array.isArray(action.payload) ? action.payload : Array.isArray(action.payload?.rows) ? action.payload.rows : [];
        const requestAssets = action.payload?.request?.assets
          || action.payload?.request?.scopes?.map((scope) => scope?.asset).filter(Boolean)
          || action.meta?.arg?.assets
          || action.meta?.arg?.scopes?.map((scope) => scope?.asset).filter(Boolean)
          || [];
        const shouldReplacePartitions = Boolean(action.payload?.meta?.isBootstrapCall);

        if (shouldReplacePartitions) {
          // Bootstrap refresh replaces full per-asset partitions so stale hydrated rows are removed.
          replaceGlobalPartitions(state.global, requestAssets);
        }

        if (rows.length > 0) {
          // Persist only canonical numeric rows; named baseline/current aliases are derived in selectors.
          mergeGlobalRows(state.global, rows);
        }

        if (shouldReplacePartitions || rows.length > 0) {
          state.meta.lastGlobalSyncAt = dayjs.utc().valueOf();
        }
      })
      .addCase(getIsolatedQuotes.fulfilled, function getIsolatedQuotesFulfilledCallback(state, action) {
        if (!action.payload || action.payload?.status === 'error') return;

        const rows = Array.isArray(action.payload) ? action.payload : Array.isArray(action.payload?.rows) ? action.payload.rows : [];
        const shouldReplaceAllIsolated = Boolean(action.payload?.meta?.isBootstrapCall);

        if (shouldReplaceAllIsolated) {
          // Isolated bootstrap refresh is authoritative for all projects; clear stale isolated rows first.
          state.isolated = {};
        }

        if (rows.length > 0) {
          mergeIsolatedRows(state.isolated, rows);
        }

        if (shouldReplaceAllIsolated || rows.length > 0) {
          state.meta.lastIsolatedSyncAt = dayjs.utc().valueOf();
        }
      });
  },
});

export default quotesSlice.reducer;
